import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Share2, Instagram, Facebook, Twitter, Youtube, MessageCircle,
  Linkedin, MessageSquare, Mail, Phone, Copy, Sparkles, Loader2,
  Package, Send, X, Check, Upload, Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ShareKitPanel — one-click promotion for a Street-Team campaign.
 *
 * Design notes (why each platform behaves the way it does):
 * - Native Share & external platform deeplinks: window.open is invoked
 *   SYNCHRONOUSLY inside the click handler. Clipboard writes happen AFTER
 *   the window opens so popup blockers never engage. Earlier version used
 *   setTimeout(() => window.open(...), 350) which dropped the user gesture
 *   and got blocked by Chrome/Safari.
 * - Instagram / YouTube / TikTok have no public share URL — we open the
 *   composer page synchronously, then copy the caption to clipboard so the
 *   promoter can paste once the composer is on screen.
 * - Email button opens an in-app dialog (paste / upload CSV / send via
 *   Resend on partner's behalf) — a real mailto: link can only BCC ~25
 *   addresses and doesn't track, so we replaced it with a server flow.
 */
export default function ShareKitPanel({ campaign, token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const url = campaign.landing_url || '';
  const baseCaption = (campaign.marketing_copy || '').replace('{LINK}', url);

  const [caption, setCaption] = useState(baseCaption);
  const [editing, setEditing] = useState(false);
  const [remixOpen, setRemixOpen] = useState(false);
  const [remixPlatform, setRemixPlatform] = useState('instagram');
  const [remixVibe, setRemixVibe] = useState('hype');
  const [remixing, setRemixing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  const track = (platform) => {
    // Fire-and-forget — never block the share itself if tracking errors.
    axios.post(`${API}/street-team/share/track`,
      { event_id: campaign.event_id, platform }, auth
    ).catch(() => {});
  };

  const copyToClipboard = async (text, label) => {
    // Best-effort: try the async API, fall back to a hidden textarea so we
    // still work in non-secure contexts (older browsers, embedded webviews).
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      if (label) toast.success(`${label} copied`);
      return true;
    } catch (_) {
      if (label) toast.error('Copy failed — long-press text to copy manually');
      return false;
    }
  };

  /** Open a platform deeplink. Synchronous window.open so popup blockers
   *  see this as a user-initiated nav. We DO NOT use setTimeout/await
   *  before opening. */
  const openPlatform = (link, platform) => {
    track(platform);
    const win = window.open(link, '_blank', 'noopener,noreferrer');
    if (!win) {
      // Popup blocked: copy the link so the partner can paste it manually.
      copyToClipboard(link, 'Share link');
      toast.message('Popup blocked — link copied. Paste it anywhere.');
    }
  };

  const captionForUrl = encodeURIComponent(caption || '');
  const urlEnc = encodeURIComponent(url);

  // -----------------------------------------------------------------------
  // Platform actions
  // -----------------------------------------------------------------------

  // Native Share — Web Share API (mobile primarily). Must be called as a
  // direct response to a user gesture, with no async work before it.
  const nativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: campaign.event?.name || 'Eventism event',
          text: caption,
          url,
        });
        track('native');
        return;
      } catch (err) {
        // user cancelled = AbortError → silent. Anything else falls through.
        if (err?.name === 'AbortError') return;
      }
    }
    // Fallback for desktop / unsupported: copy combined text and tell user.
    const ok = await copyToClipboard(`${caption}\n\n${url}`, null);
    if (ok) toast.success('Caption + link copied — paste anywhere');
  };

  // Instagram / TikTok / YouTube → open composer immediately so the popup
  // blocker is happy, THEN copy caption so user can paste.
  const instagram = () => {
    openPlatform('https://www.instagram.com/', 'instagram');
    copyToClipboard(`${caption}\n\n${url}`, 'Instagram caption');
  };
  const tiktok = () => {
    openPlatform('https://www.tiktok.com/upload', 'tiktok');
    copyToClipboard(`${caption}\n\n${url}`, 'TikTok caption');
  };
  const youtube = () => {
    openPlatform('https://studio.youtube.com/', 'youtube');
    copyToClipboard(`${caption}\n\n${url}`, 'YouTube caption');
  };

  const facebook = () =>
    openPlatform(`https://www.facebook.com/sharer/sharer.php?u=${urlEnc}&quote=${captionForUrl}`, 'facebook');
  const twitter = () =>
    openPlatform(`https://twitter.com/intent/tweet?text=${captionForUrl}&url=${urlEnc}`, 'twitter');
  const whatsapp = () =>
    openPlatform(`https://wa.me/?text=${encodeURIComponent(`${caption}\n${url}`)}`, 'whatsapp');
  const linkedin = () =>
    openPlatform(`https://www.linkedin.com/sharing/share-offsite/?url=${urlEnc}`, 'linkedin');

  const smsBlast = () =>
    openPlatform(`sms:&body=${encodeURIComponent(`${caption}\n${url}`)}`, 'sms');

  const copyLink = () => copyToClipboard(url, 'Tracking link').then(ok => ok && track('copy_link'));
  const copyCaption = () => copyToClipboard(caption, 'Caption').then(ok => ok && track('copy_caption'));

  // -----------------------------------------------------------------------
  // AI Remix
  // -----------------------------------------------------------------------

  const runRemix = async () => {
    setRemixing(true);
    try {
      const res = await axios.post(`${API}/street-team/share/remix`, {
        event_id: campaign.event_id,
        platform: remixPlatform,
        vibe: remixVibe,
      }, auth);
      setCaption(res.data.caption || caption);
      toast.success(`Remixed for ${remixPlatform} (${remixVibe})`);
      setRemixOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remix failed');
    } finally {
      setRemixing(false);
    }
  };

  // -----------------------------------------------------------------------
  // Download Kit
  // -----------------------------------------------------------------------

  const downloadKit = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/street-team/share/kit-zip?event_id=${encodeURIComponent(campaign.event_id)}`,
        { ...auth, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const safe = (campaign.event?.name || 'event').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      a.download = `${safe}-share-kit.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      toast.success('Kit downloaded — drag into your stories or group chat');
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const btn = "h-9 px-3 text-xs font-medium rounded-md flex items-center gap-1.5 border border-border bg-card hover:bg-muted/60 transition";

  return (
    <div className="space-y-2.5 border border-indigo-500/30 bg-indigo-500/5 rounded-lg p-3" data-testid={`share-kit-${campaign.event_id}`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide font-bold text-indigo-300 flex items-center gap-1.5">
          <Share2 className="h-3.5 w-3.5" /> One-click promote
        </p>
        <button
          type="button"
          onClick={() => setRemixOpen(true)}
          className="text-[11px] text-fuchsia-400 hover:text-fuchsia-300 font-semibold flex items-center gap-1"
          data-testid={`remix-open-${campaign.event_id}`}
        >
          <Sparkles className="h-3 w-3" /> AI remix caption
        </button>
      </div>

      {/* Active caption preview / editor */}
      <div className="bg-card rounded border border-border p-2 space-y-1.5">
        {editing ? (
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={4}
            className="w-full text-xs bg-muted/40 rounded p-2 font-mono"
            data-testid={`caption-edit-${campaign.event_id}`}
          />
        ) : (
          <p className="text-xs text-muted-foreground whitespace-pre-wrap font-mono leading-snug">{caption || '(no caption yet — run AI remix)'}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setEditing(!editing)} data-testid={`caption-toggle-edit-${campaign.event_id}`}>
            {editing ? <Check className="h-3 w-3 mr-1" /> : null}{editing ? 'Done editing' : 'Edit'}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={copyCaption} data-testid={`copy-caption-${campaign.event_id}`}>
            <Copy className="h-3 w-3 mr-1" /> Copy caption
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={copyLink} data-testid={`copy-link-${campaign.event_id}`}>
            <Copy className="h-3 w-3 mr-1" /> Copy link
          </Button>
        </div>
      </div>

      {/* Primary share row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button type="button" className={btn} onClick={nativeShare} data-testid={`share-native-${campaign.event_id}`}>
          <Share2 className="h-4 w-4 text-indigo-400" /> Share…
        </button>
        <button type="button" className={btn} onClick={instagram} data-testid={`share-instagram-${campaign.event_id}`}>
          <Instagram className="h-4 w-4 text-pink-400" /> Instagram
        </button>
        <button type="button" className={btn} onClick={facebook} data-testid={`share-facebook-${campaign.event_id}`}>
          <Facebook className="h-4 w-4 text-blue-500" /> Facebook
        </button>
        <button type="button" className={btn} onClick={twitter} data-testid={`share-twitter-${campaign.event_id}`}>
          <Twitter className="h-4 w-4 text-sky-400" /> X / Twitter
        </button>
        <button type="button" className={btn} onClick={youtube} data-testid={`share-youtube-${campaign.event_id}`}>
          <Youtube className="h-4 w-4 text-rose-500" /> YouTube
        </button>
        <button type="button" className={btn} onClick={whatsapp} data-testid={`share-whatsapp-${campaign.event_id}`}>
          <MessageCircle className="h-4 w-4 text-emerald-400" /> WhatsApp
        </button>
        <button type="button" className={btn} onClick={linkedin} data-testid={`share-linkedin-${campaign.event_id}`}>
          <Linkedin className="h-4 w-4 text-blue-400" /> LinkedIn
        </button>
        <button type="button" className={btn} onClick={tiktok} data-testid={`share-tiktok-${campaign.event_id}`}>
          <MessageSquare className="h-4 w-4 text-fuchsia-400" /> TikTok
        </button>
      </div>

      {/* Mass-contact row */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className={`${btn} justify-center`}
          onClick={() => setEmailOpen(true)}
          data-testid={`share-email-${campaign.event_id}`}
        >
          <Mail className="h-4 w-4 text-amber-400" /> Email blast contacts
        </button>
        <button
          type="button"
          className={`${btn} justify-center`}
          onClick={smsBlast}
          data-testid={`share-sms-${campaign.event_id}`}
        >
          <Phone className="h-4 w-4 text-emerald-400" /> Text blast from phone
        </button>
      </div>

      {/* Kit download */}
      <button
        type="button"
        onClick={downloadKit}
        disabled={downloading}
        className="w-full h-9 rounded-md bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-500 hover:to-fuchsia-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
        data-testid={`download-kit-${campaign.event_id}`}
      >
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
        {downloading ? 'Packaging…' : 'Download Share Kit (.zip)'}
      </button>

      {(campaign.share_clicks?.total || 0) > 0 && (
        <p className="text-[10px] text-muted-foreground text-center">
          <Send className="h-3 w-3 inline mr-0.5" /> You've shared this {campaign.share_clicks.total} time{campaign.share_clicks.total === 1 ? '' : 's'}
        </p>
      )}

      {remixOpen && (
        <RemixModal
          eventId={campaign.event_id}
          platform={remixPlatform} setPlatform={setRemixPlatform}
          vibe={remixVibe} setVibe={setRemixVibe}
          remixing={remixing}
          onClose={() => !remixing && setRemixOpen(false)}
          onRun={runRemix}
        />
      )}

      {emailOpen && (
        <EmailBlastModal
          campaign={campaign}
          token={token}
          baseCaption={caption}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </div>
  );
}


// ===========================================================================
// AI Remix Modal
// ===========================================================================

function RemixModal({ eventId, platform, setPlatform, vibe, setVibe, remixing, onClose, onRun }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`remix-modal-${eventId}`}
    >
      <div
        className="bg-card border border-border rounded-xl max-w-md w-full p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-fuchsia-400" /> AI Remix Caption</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground">
          Rewrite for a specific platform &amp; vibe. The AI knows what works on each (IG = no link in body, X = under 220 chars, etc.).
        </p>
        <div className="space-y-1.5">
          <label className="text-[11px] uppercase font-bold text-muted-foreground">Platform</label>
          <div className="grid grid-cols-3 gap-1.5">
            {['instagram','tiktok','twitter','facebook','youtube','sms','email'].map(p => (
              <button
                key={p}
                onClick={() => setPlatform(p)}
                className={`h-7 text-[11px] rounded border capitalize ${platform === p ? 'border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300' : 'border-border bg-muted/40'}`}
                data-testid={`remix-platform-${p}`}
              >{p}</button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] uppercase font-bold text-muted-foreground">Vibe</label>
          <div className="grid grid-cols-4 gap-1.5">
            {['hype','casual','fomo','professional'].map(v => (
              <button
                key={v}
                onClick={() => setVibe(v)}
                className={`h-7 text-[11px] rounded border capitalize ${vibe === v ? 'border-indigo-500 bg-indigo-500/10 text-indigo-300' : 'border-border bg-muted/40'}`}
                data-testid={`remix-vibe-${v}`}
              >{v}</button>
            ))}
          </div>
        </div>
        <Button
          onClick={onRun}
          disabled={remixing}
          className="w-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-500 hover:to-fuchsia-500 text-white"
          data-testid={`remix-run-${eventId}`}
        >
          {remixing ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Writing…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Remix it</>}
        </Button>
      </div>
    </div>
  );
}


// ===========================================================================
// Email Blast Modal — paste / CSV import + server send via Resend
// ===========================================================================

function EmailBlastModal({ campaign, token, baseCaption, onClose }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const eventName = campaign.event?.name || 'this event';

  const [pasted, setPasted] = useState('');
  const [subject, setSubject] = useState(`You're invited — ${eventName}`);
  const [body, setBody] = useState(
    `${baseCaption}\n\n— grab tickets here: ${campaign.landing_url || ''}`
  );
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Extract every email-shaped token from a free-form paste/CSV. We accept
  // commas, spaces, semicolons, newlines, and CSV-with-headers — the regex
  // is permissive enough that "Hey, my mom is mom@x.com & dad@x.com" works.
  const emails = React.useMemo(() => {
    const re = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g;
    const found = pasted.match(re) || [];
    const seen = new Set();
    const out = [];
    for (const e of found) {
      const lower = e.toLowerCase();
      if (!seen.has(lower)) { seen.add(lower); out.push(lower); }
    }
    return out;
  }, [pasted]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('CSV too large (max 2 MB)');
      return;
    }
    try {
      const text = await file.text();
      // Append to whatever they already pasted instead of replacing.
      setPasted(prev => (prev ? prev + '\n' : '') + text);
      toast.success('CSV imported');
    } catch (_) {
      toast.error('Could not read file');
    }
  };

  const removeEmail = (email) => {
    // Replace the address in `pasted` so it disappears from the chip list.
    const re = new RegExp(email.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    setPasted(p => p.replace(re, ''));
  };

  const send = async () => {
    if (emails.length === 0) {
      toast.error('Paste or import at least one email address');
      return;
    }
    if (subject.length < 3 || body.length < 10) {
      toast.error('Add a subject and at least a sentence of body');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/street-team/share/email-blast`, {
        event_id: campaign.event_id,
        recipients: emails,
        subject,
        body,
      }, auth);
      setResult(res.data);
      toast.success(`Sent ${res.data.sent}/${res.data.queued}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid={`email-blast-modal-${campaign.event_id}`}>
      <div className="bg-card border border-border rounded-xl max-w-xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-1.5"><Mail className="h-4 w-4 text-amber-400" /> Email blast your contacts</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground">
          Paste your contact list (or upload a CSV). We'll send each person an individual email from Eventism with your tracked link. You earn commission on every ticket sold.
        </p>

        {/* Contact input */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] uppercase font-bold text-muted-foreground">Contacts</label>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-indigo-400 hover:text-indigo-300 cursor-pointer flex items-center gap-1" data-testid={`email-blast-csv-${campaign.event_id}`}>
                <Upload className="h-3 w-3" /> Upload CSV
                <input type="file" accept=".csv,.txt" onChange={onFile} className="hidden" />
              </label>
              {pasted && (
                <button onClick={() => setPasted('')} className="text-[11px] text-rose-400 hover:text-rose-300 flex items-center gap-1" data-testid={`email-blast-clear-${campaign.event_id}`}>
                  <Trash2 className="h-3 w-3" /> Clear
                </button>
              )}
            </div>
          </div>
          <textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste emails separated by commas, spaces, or newlines. CSV with headers works too."
            rows={4}
            className="w-full text-xs bg-muted/40 rounded p-2 font-mono border border-border"
            data-testid={`email-blast-paste-${campaign.event_id}`}
          />
          <div className="flex flex-wrap gap-1.5">
            {emails.slice(0, 50).map(e => (
              <span key={e} className="inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-full px-2 py-0.5 text-[10px] font-mono" data-testid={`email-blast-chip-${e}`}>
                {e}
                <button onClick={() => removeEmail(e)} className="hover:text-rose-400"><X className="h-2.5 w-2.5" /></button>
              </span>
            ))}
            {emails.length > 50 && (
              <span className="text-[10px] text-muted-foreground">+{emails.length - 50} more</span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground" data-testid={`email-blast-count-${campaign.event_id}`}>
            {emails.length} valid email{emails.length === 1 ? '' : 's'} detected
          </p>
        </div>

        {/* Subject + body */}
        <div className="space-y-1.5">
          <label className="text-[11px] uppercase font-bold text-muted-foreground">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full text-sm bg-muted/40 rounded p-2 border border-border"
            data-testid={`email-blast-subject-${campaign.event_id}`}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] uppercase font-bold text-muted-foreground">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            className="w-full text-xs bg-muted/40 rounded p-2 border border-border"
            data-testid={`email-blast-body-${campaign.event_id}`}
          />
          <p className="text-[10px] text-muted-foreground">
            Your tracked link will be added automatically as a button at the bottom if it's not already in the body.
          </p>
        </div>

        {result && (
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200">
            ✓ Sent {result.sent} of {result.queued} emails
            {result.failed_count > 0 && <> · {result.failed_count} failed</>}
          </div>
        )}

        <Button
          onClick={send}
          disabled={sending || emails.length === 0}
          className="w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-white font-bold"
          data-testid={`email-blast-send-${campaign.event_id}`}
        >
          {sending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-1.5" /> Send to {emails.length || 0} contact{emails.length === 1 ? '' : 's'}</>}
        </Button>
      </div>
    </div>
  );
}
